import React, {useState} from 'react';
import {member} from "../../../models/member";
import {useForm} from "react-hook-form";
import {ApiError, ApiService} from "../../../service/ApiService";
import {scrollTo} from "../../../service/ToolsService";
import {Require} from "../Require";
import {NavLink, useNavigate} from "react-router-dom";
import {UploadService} from "../../../service/UploadService";

type FormDataType = {
  name?: string;
  firstname?: string;
  email?: string;
  avatar?: FileList;
}

type MemberFormProps = {
  member?: member,
}

export const MemberForm = ({member}: MemberFormProps) => {
  const navigate = useNavigate();

  const [updateImage, setUpdateImage] = useState(false);
  const [deleteImage, setDeleteImage] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: {errors}
  } = useForm<FormDataType>({
    defaultValues: {
      name: member?.name,
      firstname: member?.firstname,
      email: member?.email,
    }
  });

  const onSubmit = async (data: FormDataType) => {
    let avatar: undefined|string = undefined;

    if (deleteImage) {
      avatar = '';
    } else if (data.avatar && data.avatar.length > 0) {
      avatar = await UploadService.handleFileToBase64(data.avatar[0]);
    }

    const m = {
      name: data.name,
      firstname: data.firstname,
      email: data.email || undefined,
      avatar: avatar,
    };

    try {
      if (member) {
        await ApiService.patch(`/member/${member.memberId}`, {param: () => m})
      } else {
        await ApiService.post('/member', {param: () => m})
      }

      navigate('/admin/office/member');
      scrollTo();
    } catch (e: any) {
      const error: ApiError = e as ApiError;

      error.errors?.forEach((e: {
        field: string,
        message: string
      }) => {
        setError(e.field as "name" | "firstname" | "email", {
          type: "manual",
          message: e.message
        })
      });

    }
  }

  return (
    <form className="p-3" method="post" onSubmit={handleSubmit(onSubmit)}>
      <div className="d-flex">
        <div className="col max-w600px pe-4">
          <div className="mb-3">
            <label className="form-name" htmlFor="form-name">Nom <Require/></label>
            <input id="form-name" type="text" className="form-control" placeholder="~" {...register('name', {
              required: 'Le nom est obligatoire',
              validate: {
                minLength: (value) =>
                  (value ?? '').length <= 255 || 'Le nom ne doit pas dépasser 255 caractères',
              },
            })} />
            {errors.name && <p className="text-danger">{errors.name.message}</p>}
          </div>
          <div className="mb-3">
            <label className="form-firstname" htmlFor="form-firstname">Prénom <Require/></label>
            <input id="form-firstname" type="text" className="form-control" placeholder="~" {...register('firstname', {
              required: 'Le prénom est obligatoire',
              validate: {
                minLength: (value) =>
                  (value ?? '').length <= 255 || 'Le prénom ne doit pas dépasser 255 caractères',
              },
            })} />
            {errors.firstname && <p className="text-danger">{errors.firstname.message}</p>}
          </div>
          <div className="mb-3">
            <label className="form-email" htmlFor="form-email">Email</label>
            <input id="form-email" type="text" className="form-control" placeholder="~" {...register('email', {
              validate: {
                minLength: (value) =>
                  (value ?? '').length <= 255 || 'L\'email ne doit pas dépasser 255 caractères',
              },
            })} />
            {errors.email && <p className="text-danger">{errors.email.message}</p>}
          </div>
        </div>
        <div className="col max-w600px pe-4">
          <div className="mb-3">
            <label htmlFor="formFile" className="form-label d-block">Avatar</label>
            {member?.avatar && (
              <>
                {!updateImage && !deleteImage && (
                  <>
                    <img src={member.avatar} className="max-h250px img-fluid d-block mb-3" alt={member.name}/>
                    <button className="btn btn-xs btn-primary me-1" onClick={() => setUpdateImage(true)}>modifier</button>
                    <button className="btn btn-xs btn-danger me-1" onClick={() => setDeleteImage(true)}>supprimer</button>
                  </>
                )}

                {updateImage && (
                  <>
                    <label className="form-label italic me-2">si renseignée remplacera l'actuelle</label>
                    <button className="btn btn-xs btn-info me-1" onClick={() => setUpdateImage(false)}>annuler</button>
                  </>
                )}
                {deleteImage && (
                  <>
                    <label className="form-label italic me-2">l'image sera supprimée lors de l'enregistrement</label>
                    <button className="btn btn-xs btn-info me-1" onClick={() => setDeleteImage(false)}>annuler</button>
                  </>
                )}
              </>
            )}
            {(updateImage || !member?.avatar) && (
              <>
                <input className="form-control form-control-sm" type="file" id="formFile" {...register('avatar', {
                  validate: {
                    requiredIfUpdate: (value) =>
                      !updateImage || (value ?? []).length > 0 || 'L\'image est requise',
                  },
                })}/>
                {errors.avatar && <p className="text-danger">{errors.avatar.message}</p>}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3">
        <button className="btn btn-sm btn-success me-1" type="submit">Enregistrer</button>
        <NavLink to={`/admin/office/member`} onClick={scrollTo} className="btn btn-sm btn-info">Retour aux membres</NavLink>
      </div>
    </form>
  )
}
